import React, { useMemo, useState } from "react";
import { useCart } from "../context/CartContext";
import useProducts from "../hooks/useProducts";
import Loader from "../components/UI/Loader";
import SEO from "../components/UI/SEO";
import ProductSearch from "../components/Marketplace/ProductSearch";
import ProductFilter from "../components/Marketplace/ProductFilter";
import ProductGrid from "../components/Marketplace/ProductGrid";

const PAGE_SIZE = 9;

export default function Marketplace() {
  const { addToCart } = useCart();
  const { filteredProducts, categories, filters, setFilters, loading, error } =
    useProducts({ page_size: 100 });

  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const paginatedProducts = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredProducts.slice(start, start + PAGE_SIZE);
  }, [filteredProducts, page]);

  const handleFilterChange = (key, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddToCart = (product) => {
    addToCart(product, 1);
  };

  return (
    <>
      <SEO
        title="Marketplace Solusi IoT Cerdas"
        description="Jelajahi produk sensor, controller, smart water monitoring, smart farming, dan starter kit IoT."
        type="website"
        canonicalPath={`${window.location.origin}/marketplace`}
      />

      <section className="bg-gradient-to-br from-orange-50 via-white to-amber-50">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-600">
              Marketplace
            </p>
            <h1 className="mt-3 text-4xl font-bold tracking-tight text-gray-900">
              Produk IoT untuk monitoring, kontrol, dan otomasi
            </h1>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              Temukan produk terbaik untuk sistem monitoring air, smart farming,
              dashboard IoT, dan implementasi perangkat lapangan.
            </p>
          </div>

          <div className="mt-8 grid gap-4 rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
            <ProductSearch
              value={filters.keyword}
              onChange={(value) => handleFilterChange("keyword", value)}
            />
            <ProductFilter
              categories={categories}
              filters={filters}
              onChange={handleFilterChange}
            />
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              Menampilkan <span className="font-semibold">{filteredProducts.length}</span> produk
            </p>
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
          </div>

          <div className="mt-8">
            {loading ? (
              <Loader text="Memuat daftar produk..." />
            ) : (
              <ProductGrid products={paginatedProducts} onAddToCart={handleAddToCart} />
            )}
          </div>

          {!loading && totalPages > 1 ? (
            <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setPage(item)}
                  className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                    page === item
                      ? "bg-orange-600 text-white"
                      : "border border-gray-300 bg-white text-gray-700 hover:border-orange-400 hover:text-orange-600"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
}
